import * as React from "react";
import { Info, TriangleAlert, X } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@vbltc/ui/alert";
import { cn } from "@vbltc/ui/lib/utils";

/**
 * The one standard alert (SPEC §7.0 banner, §14.1 Recalculate prompt; CR-002 CR2-120): the
 * synthetic-data banner and the Recalculate prompt. It formats nothing; the words are the caller's.
 *
 * It is announced only when the caller says so. A banner that is on screen from the first paint is
 * read with the page; making it a live region would interrupt the reader with it again. `live` is for
 * a banner that appears because something just happened: the warning becomes `role="alert"`, the
 * info variant `role="status"`.
 */
export interface AlertBannerProps extends Omit<React.ComponentProps<"div">, "title" | "role"> {
  variant?: "info" | "warning";
  title?: string;
  /** Announce the banner when it appears (see above). */
  live?: boolean;
  /** When given, a × button dismisses the banner; the caller removes it. */
  onDismiss?: () => void;
  /** An optional control beside the message, such as the Recalculate button. */
  action?: React.ReactNode;
}

const TONE = {
  info: "bg-severity-info-bg text-severity-info-text border-severity-info",
  warning: "bg-severity-moderate-bg text-severity-moderate-text border-severity-moderate",
} as const;

export function AlertBanner({
  variant = "info",
  title,
  live = false,
  onDismiss,
  action,
  className,
  children,
  ...props
}: AlertBannerProps) {
  const Icon = variant === "warning" ? TriangleAlert : Info;
  const role = live ? (variant === "warning" ? "alert" : "status") : undefined;
  return (
    <Alert
      data-slot="alert-banner"
      data-variant={variant}
      role={role}
      className={cn(TONE[variant], onDismiss && "pr-10", className)}
      {...props}
    >
      <Icon aria-hidden="true" />
      {title ? (
        // The primitive clamps a title to one line; a clipped title reads as a different one.
        <AlertTitle className="line-clamp-none">{title}</AlertTitle>
      ) : null}
      <AlertDescription className="text-current">
        <div>{children}</div>
        {action ? <div data-slot="alert-banner-action">{action}</div> : null}
      </AlertDescription>
      {onDismiss ? (
        <button
          type="button"
          data-slot="alert-banner-dismiss"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="focus-visible:ring-ring absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      ) : null}
    </Alert>
  );
}
